import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useActivityFeed } from '@/shared/components/activity-feed/hooks/useActivityFeed';
import { useActivityFeedWithAvatars } from '@/shared/components/activity-feed/hooks/useActivityFeedWithAvatars';
import { transformEvent, isDisplayedEventType } from '@/shared/components/activity-feed/transform/eventTransformer';
import { deduplicateEvents } from '@/shared/components/activity-feed/utils/deduplicateEvents';
import { useVisibleDomains } from '@/shared/components/activity-feed/hooks/useVisibleDomains';
import { formatDistanceToNow } from '@/shared/utils/timelineHelpers';
import type { ActivityEvent } from '@/shared/types/activityEvent';

const DOMAIN_COLORS: Record<string, string> = {
  Workflow: 'text-purple-600',
  Goals: 'text-blue-600',
  Billing: 'text-green-600',
  Hiring: 'text-amber-600',
  CRM: 'text-rose-600',
  Sales: 'text-teal-600',
  Intel: 'text-indigo-600',
};

type TimeGroup = 'Today' | 'Yesterday' | 'This week' | 'Earlier';

function getTimeGroup(date: Date): TimeGroup {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const weekAgo = new Date(today.getTime() - 6 * 86400000);

  if (date >= today) return 'Today';
  if (date >= yesterday) return 'Yesterday';
  if (date >= weekAgo) return 'This week';
  return 'Earlier';
}

interface GroupedEvents {
  label: TimeGroup;
  events: ActivityEvent[];
}

function groupEventsByTime(events: ActivityEvent[]): GroupedEvents[] {
  const groupOrder: TimeGroup[] = ['Today', 'Yesterday', 'This week', 'Earlier'];
  const groups = new Map<TimeGroup, ActivityEvent[]>(
    groupOrder.map((g) => [g, []])
  );

  for (const event of events) {
    const date = event.timestamp.toDate();
    const group = getTimeGroup(date);
    groups.get(group)!.push(event);
  }

  return groupOrder
    .filter((g) => groups.get(g)!.length > 0)
    .map((g) => ({ label: g, events: groups.get(g)! }));
}

const LoadingSkeleton: React.FC = () => (
  <div className="divide-y divide-gray-100">
    {[1, 2, 3].map((i) => (
      <div key={i} className="flex items-center gap-2.5 px-3 py-2 animate-pulse">
        <div className="w-6 h-6 bg-gray-100 rounded-full flex-shrink-0" />
        <div className="flex-1 space-y-1.5">
          <div className="h-3 bg-gray-100 rounded w-3/4" />
        </div>
        <div className="h-3 bg-gray-100 rounded w-10 flex-shrink-0" />
      </div>
    ))}
  </div>
);

const EmptyState: React.FC = () => (
  <div className="flex flex-col items-center justify-center py-10 px-4">
    <p className="text-sm font-medium text-gray-700">No recent activity</p>
    <p className="text-xs text-gray-400 mt-1">
      Events from the last 14 days will appear here
    </p>
  </div>
);

export const ActivityFeedTab: React.FC = () => {
  const visibleDomains = useVisibleDomains();

  const { events: rawEvents, loading, error } = useActivityFeed('', {
    timeRange: 14,
    limit: 100,
  });

  const filteredEvents = useMemo(
    () =>
      deduplicateEvents(
        rawEvents
          .filter((e) => visibleDomains.has(e.domain))
          .filter((e) => isDisplayedEventType(e.event_type))
      ),
    [rawEvents, visibleDomains]
  );

  const enrichedEvents = useActivityFeedWithAvatars(filteredEvents);

  const transformedMap = useMemo(() => {
    const map = new Map<string, ReturnType<typeof transformEvent>>();
    enrichedEvents.forEach((event) => {
      map.set(event.id, transformEvent(event));
    });
    return map;
  }, [enrichedEvents]);

  const groupedEvents = useMemo(
    () => groupEventsByTime(enrichedEvents),
    [enrichedEvents]
  );

  return (
    <div className="flex flex-col">
      <div className="overflow-y-auto max-h-[400px]">
        {loading ? (
          <LoadingSkeleton />
        ) : error ? (
          <div className="px-4 py-8 text-center">
            <p className="text-sm text-gray-500">Unable to load activity</p>
          </div>
        ) : enrichedEvents.length === 0 ? (
          <EmptyState />
        ) : (
          <div>
            {groupedEvents.map((group) => (
              <div key={group.label}>
                <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-100">
                  <span className="text-xs font-medium text-gray-500">
                    {group.label}
                  </span>
                </div>
                <div className="divide-y divide-gray-100">
                  {group.events.map((event) => {
                    const display = transformedMap.get(event.id);
                    if (!display) return null;
                    const timestamp = event.timestamp.toDate();
                    const relativeTime = formatDistanceToNow(timestamp);
                    const domainColor =
                      DOMAIN_COLORS[display.domainLabel] || 'text-gray-500';

                    return (
                      <Link
                        key={event.id}
                        to={display.resourceLink}
                        className="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 transition-colors"
                      >
                        {/* Avatar */}
                        <div className="flex-shrink-0">
                          {event.actor.avatar_url ? (
                            <img
                              src={event.actor.avatar_url}
                              alt=""
                              className="w-6 h-6 rounded-full bg-gray-100"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                              <span className="text-[10px] font-semibold text-gray-500">
                                {event.actor.user_name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-700 truncate">
                            <span className="font-medium text-gray-900">
                              {event.actor.user_name}
                            </span>
                            {' '}
                            <span className={`font-medium ${domainColor}`}>
                              {display.domainLabel}
                            </span>
                            {' '}
                            {display.message}
                          </p>
                        </div>

                        {/* Timestamp */}
                        <time
                          className="text-[11px] text-gray-400 flex-shrink-0"
                          dateTime={timestamp.toISOString()}
                          title={timestamp.toLocaleString()}
                        >
                          {relativeTime}
                        </time>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer link */}
      <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 rounded-b-lg">
        <Link
          to="/home"
          className="text-xs text-purple-600 hover:text-purple-800 transition-colors"
        >
          View all activity
        </Link>
      </div>
    </div>
  );
};
